import { Controller } from "@hotwired/stimulus"

/**
 * Right-side drawer controller for sliding panels
 *
 * Usage:
 * <div data-controller="drawer" data-drawer-width-value="50%">
 *   <div data-drawer-target="backdrop" class="fixed inset-0 bg-black/50 hidden z-40"></div>
 *   <div data-drawer-target="panel" class="fixed right-0 top-0 h-full bg-white shadow-xl transform translate-x-full transition-transform duration-300 ease-in-out z-50">
 *     <button data-action="drawer#close">Close</button>
 *   </div>
 * </div>
 */
export default class extends Controller {
  static targets = ["panel", "backdrop"]

  static values = {
    width: { type: String, default: "50%" },
    open: { type: Boolean, default: false }
  }

  connect() {
    this.panelTarget.style.width = this.widthValue
    this.handleEscape = this.handleEscape.bind(this)

    if (this.openValue) {
      this.open()
    }
  }

  disconnect() {
    document.removeEventListener("keydown", this.handleEscape)
    document.body.classList.remove("overflow-hidden")
  }

  open() {
    this.panelTarget.classList.remove("translate-x-full")
    this.panelTarget.classList.add("translate-x-0")
    this.backdropTarget.classList.remove("hidden")
    document.body.classList.add("overflow-hidden")
    document.addEventListener("keydown", this.handleEscape)
    this.openValue = true

    // Dispatch event for other controllers to listen
    this.dispatch("opened")
  }

  close() {
    this.panelTarget.classList.add("translate-x-full")
    this.panelTarget.classList.remove("translate-x-0")
    this.backdropTarget.classList.add("hidden")
    document.body.classList.remove("overflow-hidden")
    document.removeEventListener("keydown", this.handleEscape)
    this.openValue = false

    // Dispatch event for other controllers to listen
    this.dispatch("closed")
  }

  toggle() {
    if (this.openValue) {
      this.close()
    } else {
      this.open()
    }
  }

  handleEscape(event) {
    if (event.key === "Escape") {
      this.close()
    }
  }

  // Click backdrop to close
  backdropClick(event) {
    if (event.target === this.backdropTarget) {
      this.close()
    }
  }

  // Check if drawer is open
  isOpen() {
    return this.openValue
  }
}
